// Единый слой уведомлений: фиксированный стек в левом нижнем углу, ничего не сдвигает.
// Снизу вверх: состояние связи, предохранитель записи, личное уведомление, тосты.
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { data, useData } from '../store';

export type ToastKind = 'error' | 'info';
type Toast = { id: number; text: string; kind: ToastKind };
const TOAST_MS = 5000;
let nextId = 1;

export const useNotices = create<{ toasts: Toast[]; push: (text: string, kind?: ToastKind) => void; dismiss: (id: number) => void }>((set) => ({
  toasts: [],
  push: (text, kind = 'error') => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { id, text, kind }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), TOAST_MS);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));
export const pushToast = (text: string, kind: ToastKind = 'error'): void => useNotices.getState().push(text, kind);

// Связь: короткий обрыв проходит молча; дольше DOWN_AFTER_MS — «нет связи»; вернулась после показанного
// обрыва — «восстановлено» на RESTORED_MS, чтобы было видно, что произошло, а не мигание.
export type ConnectionPhase = 'hidden' | 'down' | 'restored';
export const DOWN_AFTER_MS = 3000;
export const RESTORED_MS = 2000;
export const isDown = (c: string): boolean => c === 'reconnecting' || c === 'offline';

export function useConnectionPhase(): ConnectionPhase {
  const connection = useData((s) => s.connection);
  const [phase, setPhase] = useState<ConnectionPhase>('hidden');
  useEffect(() => {
    if (isDown(connection)) {
      const t = setTimeout(() => setPhase('down'), DOWN_AFTER_MS);
      return () => clearTimeout(t);
    }
    if (connection === 'online') {
      let t: ReturnType<typeof setTimeout> | null = null;
      setPhase((p) => {
        if (p !== 'down') return p;
        t = setTimeout(() => setPhase('hidden'), RESTORED_MS);
        return 'restored';
      });
      return () => { if (t) clearTimeout(t); };
    }
    setPhase('hidden');
    return undefined;
  }, [connection]);
  return phase;
}

const CARD = 'pointer-events-auto rounded-md px-4 py-2 font-medium shadow-lg border max-w-md';
const TONE: Record<'error' | 'info' | 'ok' | 'block', string> = {
  error: 'bg-danger text-white border-danger',
  info: 'bg-amber-100 text-amber-900 border-amber-300',
  ok: 'bg-green-100 text-green-900 border-green-300',
  block: 'bg-red-100 text-red-900 border-red-300',
};

export function NoticeStack() {
  const phase = useConnectionPhase();
  const blocked = useData((s) => s.writesBlocked);
  const notice = useData((s) => s.notice);
  const toasts = useNotices((s) => s.toasts);
  const dismiss = useNotices((s) => s.dismiss);
  return (
    <div className="fixed bottom-4 left-4 z-30 flex flex-col-reverse gap-2 pointer-events-none" data-notices>
      {phase === 'down' && (
        <div role="status" className={`${CARD} ${TONE.info}`} data-connection="down">No connection — what you see may be out of date. Reconnecting…</div>
      )}
      {phase === 'restored' && (
        <div role="status" className={`${CARD} ${TONE.ok}`} data-connection="restored">Connection restored</div>
      )}
      {blocked && <div role="alert" className={`${CARD} ${TONE.block}`}>{blocked}</div>}
      {notice && (
        <div role="alert" className={`${CARD} ${TONE.info} flex items-center gap-3`}>
          <span className="flex-1">{notice}</span>
          <button type="button" className="min-h-0 px-2 rounded hover:bg-amber-200" aria-label="Dismiss" onClick={() => data.store.setState({ notice: null })}>×</button>
        </div>
      )}
      {toasts.map((t) => (
        <div key={t.id} role="alert" className={`${CARD} ${TONE[t.kind]} flex items-center gap-3`}>
          <span className="flex-1">{t.text}</span>
          <button type="button" className={`min-h-0 px-2 rounded ${t.kind === 'error' ? 'hover:bg-white/20' : 'hover:bg-amber-200'}`} aria-label="Dismiss" onClick={() => dismiss(t.id)}>×</button>
        </div>
      ))}
    </div>
  );
}
